import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Container, Form, Row } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useAuth } from '@/shared/contexts/AuthContext';
import {
    BUILDING_OWNER_LABELS,
    institutionLabel,
} from '@/shared/constants/institutions';
import {
    CATEGORY_LABELS,
    CONDITION_LABELS,
    OPEN_STATUSES,
} from '@/shared/constants/listings';
import listingService from '@/shared/services/listingService';
import { serverMessage } from '@/shared/utils/serverMessage';
import { denverDateString } from '@/shared/utils/denverDates';
import usePageTitle from '@/shared/hooks/usePageTitle';
import { useInstitutionPolicy } from '@/shared/hooks/useInstitutionPolicy';
import { renderMarkdown } from '@/shared/utils/markdown';
import CapitalEquipmentNotice from './components/CapitalEquipmentNotice';
import DataDeviceBlock from './components/DataDeviceBlock';
import GuidelinesPanel from './components/GuidelinesPanel';
import PhotoUploader from './components/PhotoUploader';
import ValidatedField from './components/ValidatedField';
import YesNoQuestion from './components/YesNoQuestion';

// Mirrors PICKUP_MAX_DAYS in the server's listing.constants.js.
const PICKUP_MAX_DAYS = 90;

const DATA_EXAMPLES =
    'Laptops, desktop computers, hard drives, tablets, phones, and copiers or' +
    ' printers with internal storage store data. Monitors, keyboards, mice,' +
    ' docks, and headphones do not.';

const AMOUNT_HELP =
    'Per item, as acquired. Estimates power the reuse reports: pounds kept' +
    ' out of the landfill and dollars saved.';

const PICKUP_HELP =
    'Your listing closes on this date if nobody has claimed it. Leave blank' +
    ' to keep it open for 30 days.';

const BUILDING_OWNER_HELP =
    'AHEC-owned buildings are serviced by AHEC Facilities, whichever' +
    ' institution you belong to.';

// Both amounts are per item and neither is multiplied by quantity here, so a
// lot entered as its total sets the capital-equipment box on items that are
// nowhere near the threshold. CCCS SP 8-80a hangs its definition on the
// acquisition value of an asset, which is the per-item reading.
const EMPTY_FORM = {
    storesData: '',
    title: '',
    category: '',
    condition: '',
    quantity: '1',
    description: '',
    dimensions: '',
    building: '',
    room: '',
    buildingOwner: '',
    pickupNotes: '',
    estimatedValueUsd: '',
    estimatedWeightLb: '',
    isCapitalEquipment: false,
    hasAssetTag: '',
    assetTag: '',
    pickupBy: '',
    acknowledged: false,
};

// The controls a failed submit can land on, in the order the form asks for
// them. That order is what makes the first entry with an error the topmost
// problem on the page rather than an arbitrary one; a field moved in the
// markup moves here with it. A radio pair is focused on its Yes control.
const FIELD_ORDER = [
    ['storesData', 'listing-stores-data-yes'],
    ['title', 'listing-title'],
    ['category', 'listing-category'],
    ['condition', 'listing-condition'],
    ['description', 'listing-description'],
    ['building', 'listing-building'],
    ['buildingOwner', 'listing-building-owner'],
    ['hasAssetTag', 'listing-asset-tag-question-yes'],
    ['assetTag', 'listing-asset-tag'],
    ['estimatedValueUsd', 'listing-value'],
    ['estimatedWeightLb', 'listing-weight'],
    ['pickupBy', 'listing-pickup-by'],
    ['acknowledged', 'listing-acknowledge'],
];

const REQUIRED_TEXT = [
    ['title', 'Title is required.'],
    ['category', 'Choose a category.'],
    ['condition', 'Choose a condition.'],
    ['description', 'Description is required.'],
    ['building', 'Building is required.'],
    ['buildingOwner', 'Choose who services the building.'],
];

function pickupBounds() {
    return {
        min: denverDateString(new Date(), 1),
        max: denverDateString(new Date(), PICKUP_MAX_DAYS),
    };
}

// Mirrors the server's createSchema and updateSchema. Checking here keeps a
// member from losing a filled form to a round trip that fails on the first
// missing field.
function validate(form, isEdit) {
    const errors = {};
    if (!form.storesData) {
        errors.storesData = 'Answer whether this item can store data.';
    }
    for (const [field, message] of REQUIRED_TEXT) {
        if (!String(form[field] || '').trim()) {
            errors[field] = message;
        }
    }
    if (!form.hasAssetTag) {
        errors.hasAssetTag = 'Answer whether this item has an asset tag.';
    } else if (form.hasAssetTag === 'yes' && !form.assetTag.trim()) {
        errors.assetTag = 'Enter the asset tag number.';
    }
    if (form.estimatedValueUsd === '') {
        errors.estimatedValueUsd =
            'Enter an estimated value in dollars, or 0 if it has none.';
    } else if (!(Number(form.estimatedValueUsd) >= 0)) {
        errors.estimatedValueUsd = 'Estimated value cannot be negative.';
    }
    if (form.estimatedWeightLb === '') {
        errors.estimatedWeightLb = 'Enter an estimated weight in pounds.';
    } else if (!(Number(form.estimatedWeightLb) > 0)) {
        errors.estimatedWeightLb = 'Estimated weight must be more than zero.';
    }
    if (form.pickupBy) {
        const { min, max } = pickupBounds();
        if (form.pickupBy < min || form.pickupBy > max) {
            errors.pickupBy = `Choose a date from tomorrow to ${PICKUP_MAX_DAYS} days out.`;
        }
    }
    if (!isEdit && !form.acknowledged) {
        errors.acknowledged = 'Confirm that you have read the guidelines.';
    }
    return errors;
}

/**
 * Put the member on the first field that still needs an answer.
 *
 * A failed validate renders messages and moves nothing, so on a form this
 * long the member is left at the submit button with every message off screen
 * and a screen reader told nothing at all (WCAG 3.3.2). Focusing the control
 * scrolls it into view and reads its own feedback with it, because the
 * control names that feedback through aria-describedby.
 */
function focusFirstError(errors) {
    const first = FIELD_ORDER.find(([field]) => errors[field]);
    if (first) {
        document.getElementById(first[1])?.focus();
    }
}

function toForm(listing) {
    return {
        // A stored listing was accepted as a non-data item, so an edit opens
        // with the question already answered No.
        storesData: 'no',
        title: listing.title || '',
        category: listing.category || '',
        condition: listing.condition || '',
        quantity: String(listing.quantity || 1),
        description: listing.description || '',
        dimensions: listing.dimensions || '',
        building: listing.building || '',
        room: listing.room || '',
        buildingOwner: listing.buildingOwner || '',
        pickupNotes: listing.pickupNotes || '',
        estimatedValueUsd:
            listing.estimatedValueUsd === undefined
                ? ''
                : String(listing.estimatedValueUsd),
        estimatedWeightLb:
            listing.estimatedWeightLb === undefined
                ? ''
                : String(listing.estimatedWeightLb),
        isCapitalEquipment: Boolean(listing.isCapitalEquipment),
        hasAssetTag: listing.hasAssetTag ? 'yes' : 'no',
        assetTag: listing.assetTag || '',
        pickupBy: listing.pickupBy
            ? denverDateString(new Date(listing.pickupBy))
            : '',
        acknowledged: false,
    };
}

/**
 * The body each route expects. Creation carries the acknowledgment; an edit
 * carries `pickupBy` only when the member changed it, because the server holds
 * every pickup date to the tomorrow-to-90-days window and an unchanged date
 * that has since become today would be refused. Clearing a stored deadline is
 * an explicit null.
 */
function toBody(form, initialPickupBy, isEdit) {
    const body = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        condition: form.condition,
        quantity: Number(form.quantity) || 1,
        dimensions: form.dimensions.trim(),
        building: form.building.trim(),
        room: form.room.trim(),
        buildingOwner: form.buildingOwner,
        pickupNotes: form.pickupNotes.trim(),
        isCapitalEquipment: form.isCapitalEquipment,
        estimatedValueUsd: Number(form.estimatedValueUsd),
        estimatedWeightLb: Number(form.estimatedWeightLb),
        storesData: false,
        hasAssetTag: form.hasAssetTag === 'yes',
    };
    if (body.hasAssetTag) {
        body.assetTag = form.assetTag.trim();
    }
    if (isEdit) {
        if (form.pickupBy !== initialPickupBy) {
            body.pickupBy = form.pickupBy || null;
        }
    } else {
        if (form.pickupBy) {
            body.pickupBy = form.pickupBy;
        }
        body.acknowledgeGuidelines = true;
    }
    return body;
}

function toExistingPhotos(listing) {
    const urls = listing.photoUrls || [];
    return (listing.photos || []).map((photo, index) => ({
        _id: photo._id,
        url: urls[index],
    }));
}

function ListingFormPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { userProfile } = useAuth();
    const isEdit = Boolean(id);
    usePageTitle(isEdit ? 'Edit listing' : 'Post an item');

    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    // The deadline the form loaded with, so an edit can tell a changed date
    // from an untouched one.
    const [initialPickupBy, setInitialPickupBy] = useState('');
    const [files, setFiles] = useState([]);
    const [photoSelectionError, setPhotoSelectionError] = useState('');
    const [existingPhotos, setExistingPhotos] = useState([]);
    const [loading, setLoading] = useState(isEdit);
    const [closed, setClosed] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [listingInstitution, setListingInstitution] = useState('');

    // An edit follows the institution the listing was posted under, which is
    // the policy its capital-equipment notice shows; a new listing follows the
    // poster, whose institution the server copies onto it.
    const policyInstitution = isEdit
        ? listingInstitution
        : userProfile?.institution;
    const { policy } = useInstitutionPolicy(policyInstitution);
    const capitalThreshold = policy?.capitalThresholdUsd;

    useEffect(() => {
        if (!id) {
            return;
        }
        async function load() {
            try {
                const listing = await listingService.get(id);
                if (!OPEN_STATUSES.includes(listing.status)) {
                    setClosed(true);
                } else {
                    const loaded = toForm(listing);
                    setForm(loaded);
                    setInitialPickupBy(loaded.pickupBy);
                    setListingInstitution(listing.institution || '');
                    setExistingPhotos(toExistingPhotos(listing));
                }
            } catch (caught) {
                setLoadError(
                    serverMessage(caught, 'Listing not found.') ||
                        'Listing not found.'
                );
            } finally {
                setLoading(false);
            }
        }
        load();
    }, [id]);

    const setField = (name) => (event) => {
        const value =
            event.target.type === 'checkbox'
                ? event.target.checked
                : event.target.value;
        setForm((previous) => {
            const next = { ...previous, [name]: value };
            // A member pricing an item at or above the threshold is telling us
            // it is a tracked asset, so the box follows the number. It is only
            // ever set, never cleared: a sub-threshold item can still be
            // tracked, and that is the member's call to make.
            if (
                name === 'estimatedValueUsd' &&
                value !== '' &&
                capitalThreshold !== undefined &&
                Number(value) >= capitalThreshold
            ) {
                next.isCapitalEquipment = true;
            }
            return next;
        });
    };

    const setAnswer = (name) => (value) => {
        setForm((previous) => ({ ...previous, [name]: value }));
    };

    const handleRemoveExisting = async (photoId) => {
        try {
            const updated = await listingService.removePhoto(id, photoId);
            setExistingPhotos(toExistingPhotos(updated));
        } catch (caught) {
            setError(serverMessage(caught, 'Could not remove that photo.'));
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (form.storesData === 'yes') {
            return;
        }
        const nextErrors = validate(form, isEdit);
        setErrors(nextErrors);
        // A rejected photo selection blocks the save too. Posting anyway would
        // land the member on a listing with no photos, right after showing
        // them what reads as a blocking error.
        if (Object.keys(nextErrors).length > 0 || photoSelectionError) {
            focusFirstError(nextErrors);
            return;
        }
        setSaving(true);
        setError('');
        try {
            const body = toBody(form, initialPickupBy, isEdit);
            const saved = isEdit
                ? await listingService.update(id, body)
                : await listingService.create(body);
            // The listing is the thing that had to be saved. A photo upload
            // that fails afterwards is reported on the listing's own page
            // rather than discarding a successful save.
            const state = {};
            if (files.length > 0) {
                try {
                    await listingService.addPhotos(saved._id, files);
                } catch (caught) {
                    state.photoError = `The listing was ${
                        isEdit ? 'saved' : 'posted'
                    }, but the photos did not upload: ${serverMessage(
                        caught,
                        'the upload failed'
                    )}`;
                }
            }
            navigate(`/listings/${saved._id}`, { state });
        } catch (caught) {
            if (caught.response?.data?.code === 'STORES_DATA') {
                // The server holds the same rule as the question, so its
                // refusal lands on the same panel.
                setForm((previous) => ({ ...previous, storesData: 'yes' }));
            } else {
                setError(
                    serverMessage(
                        caught,
                        'Could not save the listing. Try again.'
                    )
                );
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container className="py-4">
                <LoadingSpinner message="Loading listing" />
            </Container>
        );
    }
    if (loadError) {
        return (
            <Container className="py-4">
                <Alert variant="danger">{loadError}</Alert>
                <Link to="/listings">Back to browse</Link>
            </Container>
        );
    }
    if (closed) {
        return (
            <Container className="py-4">
                <Alert variant="warning">
                    This listing is closed and cannot be edited.
                </Alert>
                <Link to={`/listings/${id}`}>Back to the listing</Link>
            </Container>
        );
    }

    const blocked = form.storesData === 'yes';
    const assetTagPolicy = policy?.assetTags;
    const pickup = pickupBounds();

    return (
        <Container className="py-4">
            <h1 className="h3 mb-4">
                {isEdit ? 'Edit listing' : 'Post an item'}
            </h1>
            <Row className="g-4">
                {/* First in the document so the rules are read before the
                    form; placed after it on wide screens. */}
                <Col lg={4} className="order-lg-last align-self-lg-start">
                    <GuidelinesPanel institution={policyInstitution} />
                </Col>
                <Col lg={8}>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <Form noValidate onSubmit={handleSubmit}>
                        <YesNoQuestion
                            controlId="listing-stores-data"
                            legend="Can this item store data?"
                            help={DATA_EXAMPLES}
                            value={form.storesData}
                            onChange={setAnswer('storesData')}
                            error={errors.storesData}
                        />
                        {blocked ? (
                            <DataDeviceBlock institution={policyInstitution} />
                        ) : (
                            <>
                                <h2 className="afe-form-section">
                                    About the item
                                </h2>
                                <ValidatedField
                                    required
                                    controlId="listing-title"
                                    label="Title"
                                    error={errors.title}
                                    value={form.title}
                                    onChange={setField('title')}
                                    maxLength={120}
                                />
                                <Row>
                                    <Col md={4}>
                                        <ValidatedField
                                            select
                                            required
                                            controlId="listing-category"
                                            label="Category"
                                            error={errors.category}
                                            value={form.category}
                                            onChange={setField('category')}
                                        >
                                            <option value="">Choose one</option>
                                            {Object.entries(
                                                CATEGORY_LABELS
                                            ).map(([key, label]) => (
                                                <option key={key} value={key}>
                                                    {label}
                                                </option>
                                            ))}
                                        </ValidatedField>
                                    </Col>
                                    <Col md={4}>
                                        <ValidatedField
                                            select
                                            required
                                            controlId="listing-condition"
                                            label="Condition"
                                            error={errors.condition}
                                            value={form.condition}
                                            onChange={setField('condition')}
                                        >
                                            <option value="">Choose one</option>
                                            {Object.entries(
                                                CONDITION_LABELS
                                            ).map(([key, label]) => (
                                                <option key={key} value={key}>
                                                    {label}
                                                </option>
                                            ))}
                                        </ValidatedField>
                                    </Col>
                                    <Col md={4}>
                                        <ValidatedField
                                            controlId="listing-quantity"
                                            label="Quantity"
                                            type="number"
                                            min={1}
                                            value={form.quantity}
                                            onChange={setField('quantity')}
                                        />
                                    </Col>
                                </Row>
                                <ValidatedField
                                    required
                                    controlId="listing-description"
                                    label="Description"
                                    error={errors.description}
                                    as="textarea"
                                    rows={4}
                                    value={form.description}
                                    onChange={setField('description')}
                                    maxLength={4000}
                                />
                                <ValidatedField
                                    controlId="listing-dimensions"
                                    label="Dimensions"
                                    value={form.dimensions}
                                    onChange={setField('dimensions')}
                                    placeholder="60 x 30 x 29 in"
                                    maxLength={200}
                                />
                                <PhotoUploader
                                    existing={existingPhotos}
                                    files={files}
                                    error={photoSelectionError}
                                    onFilesChange={setFiles}
                                    onError={setPhotoSelectionError}
                                    onRemoveExisting={handleRemoveExisting}
                                />
                                <h2 className="afe-form-section">
                                    Where it is
                                </h2>
                                <Row>
                                    <Col md={6}>
                                        <ValidatedField
                                            required
                                            controlId="listing-building"
                                            label="Building"
                                            error={errors.building}
                                            value={form.building}
                                            onChange={setField('building')}
                                            maxLength={120}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <ValidatedField
                                            controlId="listing-room"
                                            label="Room"
                                            value={form.room}
                                            onChange={setField('room')}
                                            maxLength={40}
                                        />
                                    </Col>
                                </Row>
                                <ValidatedField
                                    select
                                    required
                                    controlId="listing-building-owner"
                                    label="Building owner"
                                    error={errors.buildingOwner}
                                    help={BUILDING_OWNER_HELP}
                                    value={form.buildingOwner}
                                    onChange={setField('buildingOwner')}
                                >
                                    <option value="">Choose one</option>
                                    {Object.entries(BUILDING_OWNER_LABELS).map(
                                        ([key, label]) => (
                                            <option key={key} value={key}>
                                                {label}
                                            </option>
                                        )
                                    )}
                                </ValidatedField>
                                <ValidatedField
                                    controlId="listing-pickup-notes"
                                    label="Pickup notes"
                                    as="textarea"
                                    rows={2}
                                    value={form.pickupNotes}
                                    onChange={setField('pickupNotes')}
                                    maxLength={1000}
                                />
                                <h2 className="afe-form-section">
                                    Value, tags, and capital equipment
                                </h2>
                                <YesNoQuestion
                                    controlId="listing-asset-tag-question"
                                    legend="Does this item have an asset tag (property tag)?"
                                    value={form.hasAssetTag}
                                    onChange={setAnswer('hasAssetTag')}
                                    error={errors.hasAssetTag}
                                />
                                {form.hasAssetTag === 'yes' && (
                                    <>
                                        <ValidatedField
                                            required
                                            controlId="listing-asset-tag"
                                            label="Asset tag number"
                                            error={errors.assetTag}
                                            value={form.assetTag}
                                            onChange={setField('assetTag')}
                                            maxLength={60}
                                        />
                                        {assetTagPolicy && (
                                            <div
                                                className="mb-3"
                                                dangerouslySetInnerHTML={{
                                                    __html: renderMarkdown(
                                                        assetTagPolicy.text
                                                    ),
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                                <Row>
                                    <Col md={6}>
                                        <ValidatedField
                                            required
                                            controlId="listing-value"
                                            label="Estimated value (USD)"
                                            error={errors.estimatedValueUsd}
                                            help={AMOUNT_HELP}
                                            type="number"
                                            min={0}
                                            value={form.estimatedValueUsd}
                                            onChange={setField(
                                                'estimatedValueUsd'
                                            )}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <ValidatedField
                                            required
                                            controlId="listing-weight"
                                            label="Estimated weight (lb)"
                                            error={errors.estimatedWeightLb}
                                            help={AMOUNT_HELP}
                                            type="number"
                                            min={0}
                                            step="any"
                                            value={form.estimatedWeightLb}
                                            onChange={setField(
                                                'estimatedWeightLb'
                                            )}
                                        />
                                    </Col>
                                </Row>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        id="listing-capital"
                                        label="This is capital equipment"
                                        checked={form.isCapitalEquipment}
                                        onChange={setField(
                                            'isCapitalEquipment'
                                        )}
                                    />
                                </Form.Group>
                                {form.isCapitalEquipment && (
                                    <CapitalEquipmentNotice
                                        institution={policyInstitution}
                                    />
                                )}
                                <h2 className="afe-form-section">Pickup</h2>
                                <ValidatedField
                                    controlId="listing-pickup-by"
                                    label="Pickup deadline"
                                    error={errors.pickupBy}
                                    help={PICKUP_HELP}
                                    type="date"
                                    min={pickup.min}
                                    max={pickup.max}
                                    value={form.pickupBy}
                                    onChange={setField('pickupBy')}
                                />
                                {!isEdit && (
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            id="listing-acknowledge"
                                            label={`I have read the reuse guidelines for ${institutionLabel(
                                                userProfile?.institution
                                            )}`}
                                            checked={form.acknowledged}
                                            onChange={setField('acknowledged')}
                                            isInvalid={Boolean(
                                                errors.acknowledged
                                            )}
                                            aria-required="true"
                                            aria-invalid={
                                                errors.acknowledged
                                                    ? 'true'
                                                    : undefined
                                            }
                                            aria-describedby={
                                                errors.acknowledged
                                                    ? 'listing-acknowledge-feedback'
                                                    : undefined
                                            }
                                        />
                                        {errors.acknowledged && (
                                            <div
                                                id="listing-acknowledge-feedback"
                                                className="invalid-feedback d-block"
                                            >
                                                {errors.acknowledged}
                                            </div>
                                        )}
                                    </Form.Group>
                                )}
                                <div className="d-flex align-items-center gap-3">
                                    <Button type="submit" disabled={saving}>
                                        {isEdit
                                            ? 'Save changes'
                                            : 'Post listing'}
                                    </Button>
                                    <Link
                                        to={
                                            isEdit
                                                ? `/listings/${id}`
                                                : '/listings'
                                        }
                                    >
                                        Cancel
                                    </Link>
                                </div>
                            </>
                        )}
                    </Form>
                </Col>
            </Row>
        </Container>
    );
}

export default ListingFormPage;
